import { supabase } from '@/integrations/supabase/client';

export interface ParkCompletedResult {
  ok: boolean;
  reason?: string;
}

/**
 * Move a completed ticket to a parking spot.
 *
 * Asserts the ticket is in 'Completed' status, then routes the spot
 * assignment through the canonical `move_vehicle_location` RPC so the
 * server-side spot-occupancy check, role check, and trigger-driven
 * `vehicle_present` derivation all run. Direct `.update({ parking_spot_id })`
 * here would bypass the occupancy check and allow two completed tickets to
 * be parked in the same spot.
 *
 * Concurrency: `move_vehicle_location` is now race-safe (migration
 * 20260421230000_harden_parking_occupancy). The RPC takes a FOR UPDATE
 * lock on the target spot row and is backed by a partial UNIQUE index
 * `uniq_active_parking_spot` on `tickets(parking_spot_id)` filtered to the
 * on-premises status set with `picked_up_at IS NULL`. Concurrent claims on
 * the same spot are serialised; a slipped race is caught by the
 * constraint and returned as `{ ok:false, reason:'Parking spot is already
 * occupied' }`.
 */
export async function parkCompletedTicket(
  ticketId: string,
  spotId: string | null,
): Promise<ParkCompletedResult> {
  const { data, error: fetchError } = await supabase
    .from('tickets')
    .select('status')
    .eq('id', ticketId)
    .single();

  if (fetchError || !data) {
    console.error('[parkCompletedTicket] Failed to fetch ticket:', ticketId, fetchError);
    return { ok: false, reason: 'Failed to load ticket' };
  }

  if (data.status !== 'Completed') {
    console.error('[parkCompletedTicket] Ticket is not Completed:', ticketId, 'status:', data.status);
    return { ok: false, reason: `Ticket is ${data.status}, not Completed` };
  }

  // spotId === null intentionally maps to "general lot" via the RPC's
  // fallthrough branch (no bay, no spot, parking_lot=true). The RPC only
  // applies the spot-occupancy check when p_target_parking_spot_id is set,
  // so passing null is safe and matches the prior helper's behaviour.
  const rpcArgs: Record<string, unknown> = { p_ticket_id: ticketId };
  if (spotId) rpcArgs.p_target_parking_spot_id = spotId;
  const { data: rawData, error: rpcError } = await supabase.rpc(
    'move_vehicle_location',
    rpcArgs as any,
  );

  if (rpcError) {
    console.error('[parkCompletedTicket] RPC error:', ticketId, rpcError);
    return { ok: false, reason: rpcError.message || 'RPC error' };
  }

  const result = rawData as unknown as { success: boolean; reason?: string } | null;
  if (!result?.success) {
    console.error('[parkCompletedTicket] RPC rejected:', ticketId, result?.reason);
    return { ok: false, reason: result?.reason || 'Spot unavailable' };
  }

  return { ok: true };
}
